import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { XIcon, DownloadSimpleIcon, CopyIcon, XLogoIcon, TelegramLogoIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { stampBrandApe } from "../lib/shareLogo";

const NEON = "#00E599", DANGER = "#FF3366", WARN = "#F5A623", MUTED = "#8b8b8b";

const fmtMc = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Math.round(n)}`;
};

export const CaughtEarlyCard = ({ coin, open, onClose }) => {
  const canvasRef = useRef(null);
  const [copying, setCopying] = useState(false);

  const vel = coin.velocity || {};
  const mult = vel.multiplier || 1;
  const nowMc = coin.marketCap || 0;
  const spottedMc = mult > 0 ? nowMc / mult : nowMc;
  const symbol = (coin.symbol || "").replace("$", "");

  const shareUrl = `https://alphaapeing.com/coin/${coin.address}`;
  const shareText = `🚀 Caught $${symbol} early — ${mult}× in 1h (spotted ~${fmtMc(spottedMc)} → ${fmtMc(nowMc)}) on AlphaApeing 🦍`;

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const W = 1080, H = 1080;
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255,255,255,0.035)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y <= H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
    ctx.strokeStyle = "rgba(0,229,153,0.30)";
    ctx.lineWidth = 4;
    ctx.strokeRect(24, 24, W - 48, H - 48);

    // header wordmark
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    ctx.font = "800 46px Arial, sans-serif";
    ctx.fillStyle = "#fff";
    ctx.fillText("ALPHA", 70, 120);
    const w1 = ctx.measureText("ALPHA").width;
    ctx.fillStyle = NEON;
    ctx.fillText("APEING", 70 + w1, 120);
    ctx.font = "600 20px monospace";
    ctx.fillStyle = NEON;
    ctx.fillText("SOLANA MEME-COIN INTELLIGENCE", 72, 152);

    // "CAUGHT EARLY" tag
    ctx.font = "800 40px Arial, sans-serif";
    ctx.fillStyle = NEON;
    ctx.fillText("🚀 CAUGHT EARLY", 70, 260);

    // symbol
    ctx.fillStyle = "#fff";
    ctx.font = "800 90px Arial, sans-serif";
    ctx.fillText(`$${symbol}`, 70, 360);
    ctx.fillStyle = MUTED;
    ctx.font = "400 30px Arial, sans-serif";
    ctx.fillText((coin.name || "").slice(0, 32), 72, 405);

    // BIG multiplier
    ctx.fillStyle = MUTED;
    ctx.font = "600 26px monospace";
    ctx.fillText("MOVE IN THE LAST HOUR", 70, 530);
    ctx.fillStyle = NEON;
    ctx.font = "800 250px Arial, sans-serif";
    ctx.fillText(`${mult}×`, 62, 760);

    // spotted -> now cards
    const by = 820, bh = 130, gap = 24, bw = (W - 140 - gap) / 2, bx = 70;
    const cards = [
      { label: "SPOTTED AT ~", value: fmtMc(spottedMc), color: WARN },
      { label: "NOW ~", value: fmtMc(nowMc), color: NEON },
    ];
    cards.forEach((s, i) => {
      const x = bx + i * (bw + gap);
      ctx.strokeStyle = "rgba(255,255,255,0.12)";
      ctx.lineWidth = 2;
      ctx.strokeRect(x, by, bw, bh);
      ctx.fillStyle = MUTED;
      ctx.font = "600 22px monospace";
      ctx.fillText(s.label, x + 24, by + 46);
      ctx.fillStyle = s.color;
      ctx.font = "800 54px Arial, sans-serif";
      ctx.fillText(s.value, x + 24, by + 108);
    });

    // degen score chip
    ctx.fillStyle = MUTED;
    ctx.font = "600 24px monospace";
    ctx.fillText(`Degen Score ${coin.score?.total ?? "—"}/100 · ${coin.score?.verdict ?? ""}`, 70, 1010);

    // footer
    ctx.textAlign = "right";
    ctx.fillStyle = NEON;
    ctx.font = "700 30px monospace";
    ctx.fillText("alphaapeing.com", W - 70, 1010);
    ctx.textAlign = "left";
    stampBrandApe(c);
  }, [open, coin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const getBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

  const download = async () => {
    const blob = await getBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `alphaapeing-${symbol}-caught-early.png`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Flex card downloaded");
  };

  const copy = async () => {
    setCopying(true);
    try {
      const blob = await getBlob();
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      toast.success("Image copied — paste it into your post");
    } catch {
      toast.error("Copy not supported here — use Download instead");
    }
    setCopying(false);
  };

  const shareX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  const shareTg = () => window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, "_blank");

  return createPortal(
    <div data-testid="caught-early-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border border-white/15 max-w-md w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Flex your {mult}× on {symbol}</h3>
          <button data-testid="caught-early-close-btn" onClick={onClose} className="p-1.5 text-muted-foreground hover:text-white"><XIcon size={20} /></button>
        </div>
        <div className="border border-white/10 overflow-hidden">
          <canvas ref={canvasRef} width={1080} height={1080} className="w-full h-auto block" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="caught-early-download-btn" onClick={download} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors">
            <DownloadSimpleIcon size={16} /> Download
          </button>
          <button data-testid="caught-early-copy-btn" onClick={copy} disabled={copying} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors disabled:opacity-50">
            {copying ? <SpinnerGapIcon size={16} className="animate-spin" /> : <CopyIcon size={16} />} Copy image
          </button>
          <button data-testid="caught-early-x-btn" onClick={shareX} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-black text-sm font-semibold hover:bg-white/90 transition-colors">
            <XLogoIcon size={16} weight="fill" /> Share on X
          </button>
          <button data-testid="caught-early-tg-btn" onClick={shareTg} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#229ED9] text-white text-sm font-semibold hover:bg-[#229ED9]/90 transition-colors">
            <TelegramLogoIcon size={16} weight="fill" /> Telegram
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground text-center">"Spotted at" is the estimated market cap 1h ago (now ÷ {mult}×). Research tool · not financial advice.</p>
      </div>
    </div>,
    document.body
  );
};
